/**
 * WhatsApp Tools Server Actions (RTK Query Endpoints)
 *
 * This file contains RTK Query endpoints for WhatsApp tools in server mode.
 * These endpoints route requests through the backend -> MQTT -> Engine -> WhatsApp.
 *
 * Architecture:
 * - Each tool has a corresponding endpoint here
 * - The endpoint sends a request to the backend with a standardized naming convention
 * - The backend routes the request via MQTT to the WhatsApp engine
 * - The engine executes the tool using whatsappToolsClientActions
 *
 * Naming Convention:
 * - Endpoint name: [toolName] (e.g., getWaMeInfo)
 * - Backend action: whatsappTools.[toolName] (e.g., whatsappTools.getWaMeInfo)
 *
 */

import { appConfig } from '@common/config/app.config';
import type { UserMessage } from '@bt-common/schemas/bt/user-message';
import type { WaToolResponse } from '@bt-common/interfaces/whatsapp-tools-commands.interface';
import { apiCall } from '@extension/wa-communication-layer/api/api-call-helper';

// ============================================================================
// Types
// ============================================================================

// ── Validation Domain ────────────────────────────────────────────────────────

export interface ValidateChatIdParams {
    chatId: string;
    options?: {
        useForceUsync?: boolean;
    };
}

export interface ValidateChatIdResult {
    valid: boolean;
    chatId?: string;
    /** @deprecated Legacy alias for `chatId` emitted by pre-rename engines.
     *  Read `chatId ?? formatted_chat_id`; remove once the extension is rebuilt. */
    formatted_chat_id?: string;
}

// ── Session Domain ──────────────────────────────────────────────────────────

export interface GetMessageAckParams {
    key: string;
    from?: string;
    supportedUserId?: string;
}

export interface GetMessageAckResult {
    [key: string]: any;
}

export interface GetWaMeInfoParams {
    // No params required - the authenticated user is determined from JWT.
    // For admin/support diagnostics, `supportedUserId` is injected by the diagnostics layer.
}

export interface GetWaMeInfoResult {
    wid?: {
        _serialized: string;
        user: string;
        server: string;
    };
    name?: string;
    platform?: string;
    [key: string]: any;
}

export interface IsWhatsAppConnectedParams {
    /**
     * Optional. Only for admin/support diagnostics of another user.
     * Normal usage should omit this parameter.
     */
    supportedUserId?: string;
}

export interface IsWhatsAppConnectedResult {
    connected: boolean;
    state?: string;
    stream?: string;
    has_synced?: boolean;
}

// ── Chat Domain ─────────────────────────────────────────────────────────────

export interface GetChatObjectByChatIdParams {
    chatId: string;
}

export interface GetChatObjectByChatIdResult {
    chat: any;
}

export interface GetChatParticipantsParams {
    chatId: string;
    limit?: number;
    offset?: number;
    searchToken?: string;
}

export interface GetChatParticipantsResult {
    participants: any[];
    total?: number;
    limit?: number;
    skip?: number;
}

export interface GetCommonGroupsParams {
    contactChatId: string;
}

export interface GetCommonGroupsResult {
    groups?: any[];
    total?: number;
}

// ── Group Domain ────────────────────────────────────────────────────────────

export interface CreateGroupParams {
    groupName: string;
    participantChatIds: string[];
}

export interface CreateGroupResult {
    chatId?: string;
    isGroup?: boolean;
    inviteLink?: string;
}

export interface UpdateGroupNameParams {
    groupId: string;
    groupName: string;
}

export interface UpdateGroupNameResult {
    // empty - success conveyed by WaToolResponse wrapper
}

export interface GetGroupDataParams {
    groupId: string;
}

export interface GetGroupDataResult {
    group?: any;
}

export interface UpdateGroupDescriptionParams {
    groupId: string;
    settings: Record<string, any>;
}

export interface UpdateGroupDescriptionResult {
    // empty - success conveyed by WaToolResponse wrapper
}

export interface AddGroupParticipantParams {
    groupId: string;
    participantChatId: string;
}

export interface AddGroupParticipantResult {
    // empty - success conveyed by WaToolResponse wrapper
}

export interface RemoveGroupParticipantParams {
    groupId: string;
    participantChatId: string;
}

export interface RemoveGroupParticipantResult {
    // empty - success conveyed by WaToolResponse wrapper
}

export interface SetGroupAdminRightsParams {
    groupId: string;
    participantChatId: string;
}

export interface SetGroupAdminRightsResult {
    // empty - success conveyed by WaToolResponse wrapper
}

export interface RemoveGroupAdminRightsParams {
    groupId: string;
    participantChatId: string;
}

export interface RemoveGroupAdminRightsResult {
    // empty - success conveyed by WaToolResponse wrapper
}

export interface SetGroupPictureParams {
    groupId: string;
    fileDataUrl: string;
    fileName?: string;
    fileMimeType?: string;
}

export interface SetGroupPictureResult {
    // empty - success conveyed by WaToolResponse wrapper
}

export interface LeaveGroupParams {
    groupId: string;
}

export interface LeaveGroupResult {
    // empty - success conveyed by WaToolResponse wrapper
}

// ── Unified Contact/Chat Query Domain ──────────────────────────────────────

/**
 * Unified parameters for contact/chat queries (server mode)
 * Defaults: includeContacts=true, includeChats=true, includeLastMessage=false
 * Only pass non-default values to reduce payload size
 */
export interface QueryContactsAndChatsParams {
    /** Maximum number of results */
    limit?: number;
    /** Offset for pagination (default: 0, only pass if non-zero) */
    offset?: number;
    /** Include contacts in result (default: true, only pass if false) */
    includeContacts?: boolean;
    /** Include chats in result (default: true, only pass if false) */
    includeChats?: boolean;
    /** Include last message in chats (default: false, only pass if true) */
    includeLastMessage?: boolean;
    /** Search query - if provided, searches instead of fetching all */
    searchToken?: string;
    /** Exclude groups from results (default: false, only pass if true) */
    filterGroups?: boolean;
    /** Exclude individual contacts from results (default: false, only pass if true) */
    filterContacts?: boolean;
    /** Exclude newsletters from results (default: false, only pass if true) */
    filterNewsletters?: boolean;
}

/**
 * Unified result structure for contact/chat queries
 */
export interface QueryContactsAndChatsResult {
    /** Contacts list */
    contacts?: any[];
    /** Total contacts count */
    totalContacts?: number;
    /** Chats list */
    chats?: any[];
    /** Total chats count */
    totalChats?: number;
}

export interface GetLatestChatsAndMessagesParams {
    /** Total chats to return (pinned included). Default 50. */
    numberOfChats?: number;
    /** Latest messages per chat. Default 20. */
    numberOfMessages?: number;
}

export interface GetLatestChatsAndMessagesResult {
    chats?: any[];
    totalChats?: number;
    numberOfChats?: number;
    numberOfMessages?: number;
}

export interface SendSeenParams {
    chatId: string;
}

export interface SendSeenResult {
    // `result` is the underlying WAWeb sendSeen boolean — true when the chat
    // was actually marked as seen, false when WAWeb returned a negative result.
    result: boolean;
    error?: string;
}

// ── Chat Action Domain (clear, delete, archive, pin, mute, etc.) ───────────

export interface ChatIdParam {
    chatId: string;
}

export type ClearMessagesParams = ChatIdParam;
export interface ClearMessagesResult {
    result: boolean;
}

export type DeleteChatParams = ChatIdParam;
export interface DeleteChatResult {
    result: boolean;
}

export type ArchiveChatParams = ChatIdParam;
export interface ArchiveChatResult {
    result: boolean;
}

export type UnarchiveChatParams = ChatIdParam;
export interface UnarchiveChatResult {
    result: boolean;
}

export type PinChatParams = ChatIdParam;
export interface PinChatResult {
    result: boolean;
}

export type UnpinChatParams = ChatIdParam;
export interface UnpinChatResult {
    result: boolean;
}

export interface MuteChatParams {
    chatId: string;
    /** ISO string or milliseconds-since-epoch. Omit to mute forever. */
    unmuteDate?: string | number | null;
}
export interface MuteChatResult {
    isMuted: boolean;
    muteExpiration: number;
}

export type UnmuteChatParams = ChatIdParam;
export interface UnmuteChatResult {
    isMuted: boolean;
    muteExpiration: number;
}

export type MarkChatUnreadParams = ChatIdParam;
export interface MarkChatUnreadResult {
    result: boolean;
}

export type SendStateTypingParams = ChatIdParam;
export interface SendStateTypingResult {
    result: boolean;
}

export type SendStateRecordingParams = ChatIdParam;
export interface SendStateRecordingResult {
    result: boolean;
}

export type ClearStateParams = ChatIdParam;
export interface ClearStateResult {
    result: boolean;
}

export type GetChatLabelsParams = ChatIdParam;
export interface GetChatLabelsResult {
    labels: any[];
}

export interface ChangeLabelsParams {
    chatId: string;
    labelIds: Array<number | string>;
}
export interface ChangeLabelsResult {
    /** `true` when WAWeb accepted the label change. `false` when the
     *  underlying WAWeb call surfaced a non-throwing failure. */
    result: boolean;
    /** Set when `result === false`. The literal `'wa_business_required'`
     *  signals the connected account isn't a WhatsApp Business account
     *  (the WAWeb `addOrRemoveLabels` symbol is missing); any other value
     *  is the underlying engine diagnostic. */
    error?: string;
}

export type GetPinnedMessagesParams = ChatIdParam;
export interface GetPinnedMessagesResult {
    messages: any[];
}

export interface AddOrEditCustomerNoteParams {
    chatId: string;
    note: string;
}
export interface AddOrEditCustomerNoteResult {
    /** `true` when WAWeb accepted the note write. `false` when the
     *  underlying WAWeb call surfaced a non-throwing failure. */
    result: boolean;
    /** Set when `result === false`. The literal `'wa_business_required'`
     *  signals the connected account isn't a WhatsApp Business account
     *  (the WAWeb `smbNotesV1Enabled` symbol is missing); any other value
     *  is the underlying engine diagnostic. */
    error?: string;
}

// ── Unified Message Query Domain ───────────────────────────────────────────

/**
 * Unified parameters for message queries (server mode)
 * Consolidates: getMessages, searchMessages, getChatMessages
 *
 * Defaults:
 * - mode: 'history'
 * - slim: true (remove large bodies from media messages)
 * - includePaginationInfo: false
 */
export interface QueryMessagesParams {
    /** Chat ID - required for get mode, optional for search mode */
    chatId?: string;

    /** Search query - if provided, performs text search */
    searchQuery?: string;

    /** Maximum number of results (-1 for all) */
    limit?: number;

    /** Offset for pagination (default: 0) */
    offset?: number;

    /** Fetch direction (default: 'history') */
    mode?: 'history' | 'latest';

    /** ISO date string - get messages after this date */
    cutoffDate?: string;

    /** ISO date string - get messages before this date */
    latestDate?: string;

    /** Remove large bodies from media messages (default: true) */
    slim?: boolean;

    /** Get messages from end of chat */
    tail?: boolean;

    /** Include hasMore flag for pagination (default: false) */
    includePaginationInfo?: boolean;
}

/**
 * Unified result structure for message queries
 */
export interface QueryMessagesResult {
    /** Messages list */
    messages: any[];
    /** Total messages in store */
    totalMessages: number;
    /** Paging info */
    paging: {
        /** Current offset */
        offset: number;
        /** Current limit */
        limit: number;
        /** More messages available in local store */
        hasMoreInStore: boolean;
        /** Can load more messages from phone */
        canLoadFromPhone: boolean;
        /** Whether loadFromPhoneIfNeeded was triggered */
        isLoadFromPhoneIfNeededTriggered: boolean;
        /** Number of messages added by loading from phone */
        totalMessagesAddedByLoadFromPhoneIfNeeded: number;
    };
}

export interface LoadMessagesFromPhoneParams {
    chatId: string;
}

export interface LoadMessagesFromPhoneResult {
    totalMessages: number;
    totalMessagesAddedByLoadFromPhoneIfNeeded: number;
    canLoadMore: boolean;
    /** Set when the phone could not serve older history (no retrievable
     *  history, offline, or no response within the poll window). Surfaced to
     *  the /v1/messages/load_older API as `history_unavailable` + `error`. */
    error?: string | null;
}

export interface GetMessageMediaResult {
    mimetype?: string;
    data?: string;
    filename?: string;
}

/**
 * Result for getting message media URL (cached version)
 * Returns URL instead of base64 data for efficient display
 */
export interface GetMessageMediaUrlResult {
    mediaUrl?: string;
    fileName?: string;
    fileType?: string;
    fileSize?: number;
}

export interface GetMessagesAckDataParams {
    waMessagesKeys: string[];
}

export interface GetMessagesAckDataResult {
    ackData: any[];
}

export interface GetMessageByKeyResult {
    message: any;
}

export interface ReactToMessageParams {
    waMessageKey: string;
    text: string;
}

export interface ReactToMessageResult {
    // empty - success conveyed by WaToolResponse wrapper
}

export interface PinMessageParams {
    waMessageKey: string;
    duration?: number;
}

export interface PinMessageResult {
    success?: boolean;
}

export interface UnpinMessageParams {
    waMessageKey: string;
}

export interface UnpinMessageResult {
    success?: boolean;
}

export interface CreateStatusParams {
    supportedUserId?: string;
    /** FE#360 — scheduled `userMessage._id`, carried into the Status's
     *  messageSecret tag so the duplicate guard can see a prior post. */
    userMessageId?: string;
    text?: string;
    content?: string;
    fileDataUrl?: string;
    fileName?: string;
    fileMimeType?: string;
}

export interface CreateStatusResult {
    action?: 'text' | 'media';
    messageId?: string;
}

export interface SendMessageParams {
    /**
     * Unified raw WA payload (preferred for diagnostics and server dispatch).
     */
    waMsg?: any;
    chatId?: string;
    userMessageId?: string;
    /**
     * Backward-compatible wrapper payload used by sendMessageThunkTool.
     */
    userMessage?: UserMessage;
    isSendMessageNow?: boolean;
}

export interface SendMessageResult {
    messageId?: string;
}


import { logTabWakeEvent } from "@common/services/tab-wake-logger";

function logServerToolEvent(eventType: string, extra: Record<string, any> = {}) {
    logTabWakeEvent({ eventType, source: 'client', ...extra });
}

export function extractToolErrorMessage(error: any, fallback = 'Server tool request failed'): string {
    if (!error) return fallback;
    if (typeof error === 'string' && error.trim()) return error;
    if (typeof error?.message === 'string' && error.message.trim()) return error.message;
    if (typeof error?.error === 'string' && error.error.trim()) return error.error;
    if (typeof error?.data?.message === 'string' && error.data.message.trim()) return error.data.message;
    if (typeof error?.data?.error === 'string' && error.data.error.trim()) return error.data.error;
    if (typeof error?.data?.error?.message === 'string' && error.data.error.message.trim()) return error.data.error.message;
    if (typeof error?.response?.data?.message === 'string' && error.response.data.message.trim()) return error.response.data.message;
    if (typeof error?.response?.data?.error === 'string' && error.response.data.error.trim()) return error.response.data.error;
    if (typeof error?.response?.data?.error?.message === 'string' && error.response.data.error.message.trim()) return error.response.data.error.message;
    try {
        const serialized = JSON.stringify(error);
        if (serialized && serialized !== '{}') return serialized;
    } catch {
        // Fall through to String(error)
    }
    const asString = String(error);
    return asString && asString !== '[object Object]' ? asString : fallback;
}

export async function triggerRequesthWhatsappTool<TParams, TResult>(
    cmd: string,
    params?: TParams
): Promise<WaToolResponse<TResult>> {
    const headers: Record<string, string> = {};

    try {
        const search = new URLSearchParams(window.location.search);
        const supportedUserId = search.get('supportedUserId');
        const supportedWorkSpaceId = search.get('workspaceId');
        if (supportedUserId) headers.supportedUserId = supportedUserId;
        if (supportedWorkSpaceId) headers.supportedWorkSpaceId = supportedWorkSpaceId;
    } catch {
        // Best-effort only.
    }

    const t0 = Date.now();
    logServerToolEvent('SERVER_TOOL_REQUEST_START', { tool: cmd });
    try {
        const result = await apiCall<any>('post', `/actions?cmd=${encodeURIComponent(cmd)}`, params || {}, { headers });
        logServerToolEvent('SERVER_TOOL_REQUEST_END', { tool: cmd, durationMs: Date.now() - t0, success: true });
        // If the result is already a WaToolResponse (from notifier handler), pass through
        if (result && typeof result === 'object' && 'success' in result && ('data' in result || 'error' in result)) {
            return result as WaToolResponse<TResult>;
        }
        return { success: true, data: result } as WaToolResponse<TResult>;
    } catch (error: any) {
        const message = extractToolErrorMessage(error);
        logServerToolEvent('SERVER_TOOL_REQUEST_END', { tool: cmd, durationMs: Date.now() - t0, success: false, error: message });
        return { success: false, error: { code: 'SERVER_ERROR', message } };
    }
}
